/* eslint-disable @typescript-eslint/no-explicit-any */
import every from 'lodash/every';
import get from 'lodash/get';
import moment from 'moment';
import { hasRequiredTransportData, isTransportRequired } from './carbonFootprint';
import { getEmailFieldDomainRegex, validateEmail } from './email';
import { extractRequiredFields } from './fields';
import { filterOptions, isValidMultiRadio } from './options';
import { isValidPhoneValue } from './phone';
import { computeMinMaxCount } from './template';

/**
 * Validator of a field type, used instead of the built-in rule.
 * Lets a renderer plug the checks of its own complex fields (rooming...)
 * that depend on its runtime (current participant, browser APIs).
 */
export type RequiredFieldValidator = (field: any, data: any, context: RequiredRulesContext) => boolean;

export type RequiredRulesContext = {
  validators?: Record<string, RequiredFieldValidator>;
  /** Whether the participant can no longer change the field (renderer specific, e.g. `locked` fields already answered) */
  isLocked?: (field: any) => boolean;
};

export type MissingField = { name: string; label: string; type: string };

export function validateListItems(listItemField: any, items: any[], parentData: any, context?: RequiredRulesContext): boolean {
  const { fields } = listItemField;

  const minItems = computeMinMaxCount(listItemField.minItems, parentData);
  const maxItems = computeMinMaxCount(listItemField.maxItems, parentData);

  if (minItems && items.length < minItems) return false;
  if (maxItems && items.length > maxItems) return false;

  for (const item of items) {
    if (!hasRequiredData(fields, item, parentData, context)) {
      return false;
    }
  }
  return true;
}

/** Whether a required field holds a valid value. */
export function isValidRequiredField(field: any, data: any, context: RequiredRulesContext = {}): boolean {
  const validator = context.validators?.[field.type];
  if (validator) return validator(field, data, context);

  switch (field.type) {
    case 'PhoneNumberField':
      return isValidPhoneValue(data[field.name], field.defaultValue);
    case 'EmailField': {
      const email = data[field.name];
      if (!email) return false;
      const { isValid } = validateEmail(email, getEmailFieldDomainRegex(field));
      return isValid;
    }
    case 'DateSelectField': {
      const { name, format = 'YYYY-MM-DD' } = field;
      const date = data[name];
      if (!date) return false;
      return moment(date, format).isValid();
    }
    case 'ListItem': {
      const items = data[field.name] || [];
      return validateListItems(field, items, data, context);
    }
    case 'RadioField': {
      if (!field.allowMultiple) return !!get(data, field.name);
      // A locked answer can't be changed (and hides its error message): never block on its minCount
      if (context.isLocked?.(field)) return true;
      // Same options as displayed, so that the capped minCount matches the field's error message
      return isValidMultiRadio({ ...field, options: filterOptions(data, field.options || []) }, get(data, field.name));
    }
    case 'MultiSelectField': {
      const { minItems, maxItems, name } = field;
      const items = data[name] || [];
      if (minItems && items.length < minItems) return false;
      if (maxItems && items.length > maxItems) return false;
      return items.length > 0;
    }
    case 'WorkshopRegistrationV2': {
      const { workshopsRegistrations = [] } = data;
      const { minCount, required } = field;
      const selectedWorkshops = workshopsRegistrations?.filter((registration: any) =>
        field.workshops?.some((w: any) => w._id === registration.sessionId),
      );
      return (!required || selectedWorkshops.length > 0) && (!minCount || selectedWorkshops.length >= Number(minCount));
    }
    case 'FlightField': {
      const { name, required } = field;
      const flightData = data[name];
      if (!required) return true;
      return flightData?.steps?.length > 0;
    }
    case 'CarbonFootprintTransportField': {
      const transport = get(data, field.name);
      if (!isTransportRequired(field.required, transport)) return true;
      return hasRequiredTransportData(transport);
    }
    case 'TownsField': {
      const { cpName, townName } = field;
      const cpValue = data[cpName];
      const townValue = data[townName];
      return !!cpValue && !!townValue;
    }
    default: {
      // 0 is an answer (a slider or a number set to 0); false stays empty (an unchecked checkbox)
      const value = get(data, field.name);
      return value === 0 || !!value;
    }
  }
}

export function hasRequiredData(fields: any[] | null | undefined, data: any, parentData?: any, context?: RequiredRulesContext): boolean {
  return every(extractRequiredFields(fields, data, parentData), (field) => isValidRequiredField(field, data, context));
}

/** List of missing required fields with their names and labels. */
export function getMissingRequiredFields(
  fields: any[] | null | undefined,
  data: any,
  parentData?: any,
  context?: RequiredRulesContext,
): MissingField[] {
  const requiredFields = extractRequiredFields(fields, data, parentData);
  const missingFields: MissingField[] = [];

  for (const field of requiredFields) {
    if (!isValidRequiredField(field, data, context)) {
      missingFields.push({
        name: field.name,
        label: field.label || field.name,
        type: field.type,
      });
    }
  }

  return missingFields;
}
